import cx from "classnames";

import { IItemWithProduct, IPatient } from "common/types";
import { toDollars } from "common/utils/formatting";
import CatalogImage from "common/components/CatalogImage";

import CrossedPrice from "client/common/components/content/CrossedPrice";

import { IOnboardingSubscription } from "../types";

import styles from "../styles/OnboardingSubscriptionItem.module.less";

type Subscription = {
  item: IItemWithProduct;
  quantity: number;
  patient: IPatient;
};

type OnboardingSubscriptionProps = {
  subscription: Subscription;
  renderSideContent?: () => React.ReactNode;
  renderProductContent?: () => React.ReactNode;
  children?: React.ReactNode;
  includeQuantity?: boolean;
  imageSize?: "s" | "m";
  isBoxed?: boolean;
};

const OnboardingSubscriptionItem = ({
  subscription,
  renderSideContent = () => null,
  renderProductContent = () => null,
  children,
  includeQuantity = true,
  imageSize = "m",
  isBoxed = true,
}: OnboardingSubscriptionProps) => (
  <div className={cx(styles.subscription, isBoxed && styles.boxed)}>
    <div className={styles.content}>
      <div className={styles.product}>
        <CatalogImage
          image={subscription.item.image}
          alt={subscription.item.full_name}
          size="thumbnail"
          className={styles[`imageSize-${imageSize}`]}
        />
        <div className={styles.info}>
          <div>
            <p className={styles.title}>{subscription.item.product.product_line.name}</p>
            <p className={styles.description}>{subscription.item.product_line_relative_name}</p>
            <p className={styles.description}>
              For {subscription.patient.name}
              {includeQuantity && ` · Qty: ${subscription.quantity}`}
            </p>
          </div>
          {renderProductContent()}
        </div>
      </div>
      {renderSideContent()}
    </div>
    {children}
  </div>
);

type OnboardingSubscriptionItemPricingProps = {
  subscription: IOnboardingSubscription;
};

const OnboardingSubscriptionItemPricing = ({
  subscription,
}: OnboardingSubscriptionItemPricingProps) => {
  // some subscriptions aren't eligible for subscription discounts
  const hasDiscount =
    subscription.subtotal_with_first_discount < subscription.subtotal ||
    subscription.subtotal_with_recurring_discount < subscription.subtotal;
  const hasDifferentFirstRecurringDiscount =
    subscription.first_discount_value !== subscription.recurring_discount_value;
  const priceRows = [
    {
      label: `${hasDifferentFirstRecurringDiscount ? "First time " : ""}Autoship price`,
      value: (
        <div className={styles.firstPrice}>
          {toDollars(subscription.subtotal_with_first_discount)}
        </div>
      ),
      visible: true,
    },
    {
      label: "Recurring Autoship price",
      value: toDollars(subscription.subtotal_with_recurring_discount),
      visible: hasDifferentFirstRecurringDiscount,
    },
    {
      label: "Regular price",
      value: (
        <CrossedPrice classNames={{ crossed: styles.crossed }} crossed={subscription.subtotal} />
      ),
      visible: hasDiscount,
    },
  ];

  return (
    <div className={styles.prices}>
      {priceRows
        .filter(({ visible }) => visible)
        .map(({ label, value }) => (
          <div key={label} className={styles.price}>
            <div className={styles.label}>{label}</div>
            <div className={styles.value}>{value}</div>
          </div>
        ))}
    </div>
  );
};

export default Object.assign(OnboardingSubscriptionItem, {
  Pricing: OnboardingSubscriptionItemPricing,
});
